/** @jsx jsx */
import React, { Suspense, useState } from 'react'
import { useWindowSize } from 'react-use'
import { jsx } from 'theme-ui'
import { Box, SEO, TextOnTable } from '../../components'
import { ButtonBlock } from '../../components/ButtonBlock'
import { FormattedUserName } from '../../components/FormattedUserName'
import { numericBreakpoints } from '../../gatsby-plugin-theme-ui/theme'
import { BottomOfPageDetails } from './BottomOfPageDetails'
import { FinalPokerChipPots, InGamePokerChipPots } from './CommunalPokerChipPots'
import { CurrentActionButton } from './CurrentActionButton'
import { CurrentUserHand } from './CurrentUserHand'
import { DealerButton } from './DealerButton'
import { GameChat } from './GameChat'
import { GameFAQModal } from './GameFAQModal'
import { OtherPlayerInformation } from './OtherPlayerInformation'
import { PlayingCardsBeingDealt } from './PlayingCardsBeingDealt'
import { usePositionProps } from './positionProps'
import { Table } from './Table'
import { GameMockType } from './types'
import { IUseGameActionsFn } from './useGameActions'
import { IGameData } from './useGameData'
import { useGetController } from './useGetController'
import { UsersCurrentTableAction } from './UsersCurrentTableAction'
import { VisibleSolvedUserHands } from './VisibleSolvedUserHands'
import { PositioningDebugger } from '../../components/PositioningDebugger'

const LazyGameHasFinishedConfetti = React.lazy(() => import('./GameHasFinishedConfetti'))

export const GameDetailsActiveGame: React.FC<{
  gameData: IGameData
  getActions: IUseGameActionsFn
  gameMockType?: GameMockType
}> = ({ gameData, getActions, gameMockType }) => {
  const { game } = gameData
  const windowSize = useWindowSize()
  const hasChatThatOverlays = windowSize.width <= numericBreakpoints[4]
  const [hasGameChatVisible, setHasGameChatVisible] = useState(!hasChatThatOverlays)
  const otherUserCount = gameData.currentHand.userTablePosition.length - 1
  const positionProps = usePositionProps({
    otherUserCount,
    hasGameChatVisible,
    windowSize,
  })
  const { currentUserTablePositioning, otherUserPositioning, measurements } = positionProps
  const { controller, previousController } = useGetController(gameData, positionProps)
  const { currentUserData } = controller
  const actions = getActions(controller)
  // return <PositioningDebugger {...{ ...positionProps, otherUserCount }} />
  return (
    <Box bg="background">
      <SEO title="Your Poker Game" noIndex />
      {!!controller.gameHand.finalResult?.gameHasEndedChipAmounts && (
        <Suspense fallback={null}>
          <LazyGameHasFinishedConfetti windowSize={windowSize} />
        </Suspense>
      )}

      <GameChat
        {...{ measurements, gameData, controller, setHasGameChatVisible, hasGameChatVisible }}
        sx={{ left: hasGameChatVisible ? 0 : -300, transition: 'left .3s linear' }}
      />
      <Box
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          opacity: hasGameChatVisible ? 0 : 1,
          transition: 'opacity .3s linear',
          zIndex: 1,
        }}
      >
        <ButtonBlock
          onClick={() => setHasGameChatVisible(true)}
          sx={{
            p: [3, null, null, null, 4],
            textAlign: 'left',
            color: 'lightText',
            fontSize: [1, null, null, 2],
            borderRadius: 'lg',
          }}
        >
          Open History &#8594;
        </ButtonBlock>
      </Box>
      <Box
        css={{
          width: hasGameChatVisible && !hasChatThatOverlays ? 'calc(100% - 300px)' : '100%',
          position: 'relative',
          left: hasGameChatVisible && !hasChatThatOverlays ? 300 : 0,
          transition: 'all .3s ease-in',
          height: windowSize.height,
        }}
      >
        <Box
          mx="auto"
          css={{ width: measurements.tableAndPlayersWidth, position: 'relative', height: '100%' }}
        >
          <GameFAQModal {...{ controller }} />
          <Box css={{ height: measurements.tableAndPlayersHeight }}>
            <Table measurements={measurements}>
              <Box>
                {!!controller.gameHand.finalResult?.gameHasEndedChipAmounts ? (
                  <TextOnTable sx={{ fontSize: [1, 1, 2, 3] }}>
                    Game Has Ended - Congrats{' '}
                    <FormattedUserName
                      user={controller.getUserData(controller.getWinningUserId())}
                    />
                    !
                  </TextOnTable>
                ) : (
                  <InGamePokerChipPots {...{ ...positionProps, controller, previousController }} />
                )}
                <PlayingCardsBeingDealt
                  measurements={measurements}
                  cards={controller.gameHand.visibleCards}
                />
              </Box>
            </Table>
            {!!controller.gameHand.finalResult && (
              <FinalPokerChipPots {...{ ...positionProps, controller, previousController }} />
            )}
            <DealerButton {...positionProps} controller={controller} />
            <CurrentActionButton {...positionProps} controller={controller} />
            <VisibleSolvedUserHands {...{ ...positionProps, controller }} />
            {controller.otherUsersTablePosition.map((userId, index) => {
              return (
                <React.Fragment key={index}>
                  <OtherPlayerInformation
                    {...{
                      userId,
                      measurements,
                      controller,
                      actions,
                    }}
                  />
                  <UsersCurrentTableAction
                    {...{
                      userId,
                      measurements,
                      controller,
                      previousController,
                    }}
                  />
                </React.Fragment>
              )
            })}
            <UsersCurrentTableAction
              {...{
                userId: currentUserData.id,
                measurements,
                controller,
                previousController,
              }}
            />
          </Box>
          <Box>
            <Box mx="auto" sx={{ maxWidth: '800px', textAlign: 'center' }} py={4}>
              <CurrentUserHand {...{ controller, actions, gameMockType, measurements }} />
            </Box>
          </Box>
        </Box>
        <BottomOfPageDetails {...{ controller, actions, gameMockType, measurements }} />
      </Box>
    </Box>
  )
}
